import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import SearchBar from './SearchBar';
import BidDetailModal from './BidDetailModal';
import { ArrowDown, ArrowUp, Loader } from 'react-feather';
import 'chart.js/auto';
import "../../css/global.css"
import { Spin } from "antd";

const useQuery = () => new URLSearchParams(useLocation().search);

const CompanySearchPage = () => {
    const query = useQuery();
    const bizNumber = query.get("bizNumber");

    const [company, setCompany] = useState(null);
    const [stats, setStats] = useState(null);
    const [progress, setProgress] = useState(null);
    const [selected, setSelected] = useState(null);
    const [showOnlyWon, setShowOnlyWon] = useState(false);

    // 정렬 상태
    const [sortKey, setSortKey] = useState('bidOpenDate');
    const [sortAsc, setSortAsc] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (bizNumber) handleSearch(bizNumber);
        // eslint-disable-next-line
    }, [bizNumber]);

    const handleSearch = async (businessNumber) => {
        setLoading(true);
        setStats(null);
        setCompany(null);
        setProgress(null);

        try {
            // bidding-results에서 SSE 혹은 통계+목록+통계 DTO 모두 처리
            const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results?bizNumber=${businessNumber}`);

            // SSE 처리: 만약 res.data에 progress, done, 등 실시간 작업 플래그가 있다면
            if (res.data?.working || res.status === 202) {
                // (실제 작업중이면 서버에서 202로 내려주는 방식도 있음)
                // 실제로는 일반적으로 fetch로 직접 스트림 받아야 하지만, 기존 방식대로 진행 (상황 맞게 조정)
                // 단순 예시로 진행 (기존 SSE 핸들링과 동일)
                const token = localStorage.getItem("auth_token");
                const es = new EventSource(
                    `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results/stream?bizNumber=${businessNumber}&token=${token}`
                );

                es.addEventListener("progress", (e) => {
                    setProgress(JSON.parse(e.data));
                });

                es.addEventListener("done", async () => {
                    es.close();
                    setProgress(null);
                    // 작업 완료 후, 통계+결과 다시 받아오기
                    const statsRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results?bizNumber=${businessNumber}`);
                    setStats(statsRes.data);
                });

                es.onerror = () => es.close();

            } else if (res.data?.allResults) {
                // 이미 준비되어서 DTO로 바로 받는 경우
                setStats(res.data);
            } else {
                // 비정상: 에러 안내
                setStats(null);
            }

            // 회사 기본정보 조회
            const companyInfo = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/companies/info?bizNumber=${businessNumber}`);
            setCompany(companyInfo.data);

            setLoading(false);

        } catch (err) {
            setLoading(false);
            setStats(null);
            setCompany(null);
            setProgress(null);
            console.error("❌ 데이터 조회 실패:", err);
        }
    };

    // 정렬 함수
    const handleSort = (key) => {
        if (sortKey === key) {
            setSortAsc(!sortAsc);
        } else {
            setSortKey(key);
            setSortAsc(true);
        }
    };

    // 표시할 목록
    const displayResults = [
        ...(showOnlyWon ? stats?.wonResults || [] : stats?.allResults || [])
    ].sort((a, b) => {
        // null check
        if (a[sortKey] == null || b[sortKey] == null) return 0;
        if (typeof a[sortKey] === 'string') {
            return sortAsc
                ? a[sortKey].localeCompare(b[sortKey])
                : b[sortKey].localeCompare(a[sortKey]);
        } else {
            return sortAsc
                ? a[sortKey] - b[sortKey]
                : b[sortKey] - a[sortKey];
        }
    });

    // 정렬 아이콘
    const renderSortIcon = (key) => {
        if (sortKey !== key) return null;
        return sortAsc ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
    };

    // 다운로드 함수
    const handleDownload = async () => {
        if (!bizNumber) return;
        setLoading(true);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results/export?bizNumber=${bizNumber}`,
                { responseType: 'blob' }
            );
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bidding-results-${bizNumber}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("엑셀 다운로드 실패:", error);
        }
        setLoading(false);
    };

    return (
        <Spin spinning={loading} tip="로딩 중입니다..." size="large">
            <div className="slow-animation p-4 space-y-6 max-w-7xl mx-auto">

                {/* 검색 */}
                <SearchBar onSelect={(bizNumber) =>
                    window.location.href = `/search?type=${encodeURIComponent('기업 검색')}&bizNumber=${bizNumber}`
                } />

                {/* 회사 정보 */}
                {company ? (
                    <section className="border rounded-xl p-4 shadow bg-white">
                        <h2 className="text-xl font-bold">{company.name}</h2>
                        <p className="text-gray-600 text-sm">사업자번호: {company.businessNumber}</p>
                    </section>
                ) : (
                    <section className="text-gray-500 text-sm bg-white p-4 rounded-xl shadow border flex items-center gap-2">
                        <Loader className="animate-spin w-4 h-4" /> 회사 정보 로딩 중입니다...
                    </section>
                )}

                {/* 진행중 표시 */}
                {progress && (
                    <section className="border p-4 text-center bg-white rounded-xl shadow">
                        <h3 className="text-lg font-semibold mb-2">
                            낙찰 데이터 수집 중... (페이지 이탈 후 다른 작업하고 오셔도 괜찮습니다)
                        </h3>
                        <p className="text-gray-700">
                            {progress.fetched}/{progress.total} 건 완료 (
                            {progress.progressPercent?.toFixed(1)}%)
                        </p>
                    </section>
                )}

                {/* 통계 */}
                {stats ? (
                    <section className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-4 justify-between items-center">
                        <div className="text-center flex-1 min-w-[150px]">
                            <div className="text-sm text-gray-600">총 투찰</div>
                            <div className="text-xl font-bold">{stats.totalBidCount}건</div>
                        </div>
                        <div className="text-center flex-1 min-w-[150px]">
                            <div className="text-sm text-gray-600">총 투찰 금액</div>
                            <div className="text-xl font-bold">{stats.totalBidAmount ? stats.totalBidAmount.toLocaleString() : 0}원</div>
                        </div>
                        <div className="text-center flex-1 min-w-[150px]">
                            <div className="text-sm text-gray-600">낙찰 건수</div>
                            <div className="text-xl font-bold text-red-600">{stats.totalWinCount}건</div>
                        </div>
                        <div className="text-center flex-1 min-w-[150px]">
                            <div className="text-sm text-gray-600">낙찰 금액</div>
                            <div className="text-xl font-bold">{stats.totalWinAmount ? stats.totalWinAmount.toLocaleString() : 0}원</div>
                        </div>
                        <div className="text-center flex-1 min-w-[150px]">
                            <div className="text-sm text-gray-600">낙찰률</div>
                            <div className="text-xl font-bold text-blue-600">{stats.winRate?.toFixed(1)}%</div>
                        </div>
                    </section>
                ) : (
                    !progress && (
                        <section className="text-gray-500 text-sm bg-white p-4 rounded-xl shadow border flex items-center gap-2">
                            <Loader className="animate-spin w-4 h-4" /> 통계 정보 로딩 중입니다...
                        </section>
                    )
                )}

                {/* 입찰 결과 목록 + 다운로드 버튼 */}
                {stats?.allResults ? (
                    <section className="bg-white mt-6 p-6 rounded-xl shadow">
                        <div className="flex flex-wrap justify-between items-center mb-4">
                            <div className="flex items-center gap-4">
                                <h3 className="text-xl font-bold">입찰 결과 목록</h3>
                                <span className="text-sm text-gray-400">
                                    (낙찰 업체가 없는 건 낙찰 결과가 없는 것입니다)
                                </span>
                            </div>
                            <div className="flex items-center gap-4">
                                {/* 낙찰만 보기 체크박스 */}
                                <label className="text-sm flex items-center gap-1">
                                    <input
                                        type="checkbox"
                                        checked={showOnlyWon}
                                        onChange={() => setShowOnlyWon(!showOnlyWon)}
                                    />
                                    낙찰된 공고만
                                </label>

                                {/* 다운로드 버튼 */}
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded"
                                >
                                    결과 엑셀 다운로드
                                </button>
                            </div>
                        </div>

                        <table className="w-full text-sm border-collapse">
                            <thead>
                            <tr className="bg-gray-50 border-b">
                                <th className="px-3 py-2 text-left cursor-pointer"
                                    onClick={() => handleSort("bidTitle")}>
                                        <span className="inline-flex items-center gap-1">
                                            공고명
                                            {renderSortIcon("bidTitle")}
                                        </span>
                                </th>
                                <th className="px-3 py-2 text-left cursor-pointer"
                                    onClick={() => handleSort("bidOpenDate")}>
                                        <span className="inline-flex items-center gap-1">
                                            개찰일
                                            {renderSortIcon("bidOpenDate")}
                                        </span>
                                </th>
                                <th className="px-3 py-2 text-left cursor-pointer"
                                    onClick={() => handleSort("winningCompanyName")}>
                                        <span className="inline-flex items-center gap-1">
                                            낙찰업체
                                            {renderSortIcon("winningCompanyName")}
                                        </span>
                                </th>
                                <th className="px-3 py-2 text-right cursor-pointer"
                                    onClick={() => handleSort("winningPrice")}>
                                        <span className="inline-flex items-center gap-1 justify-end w-full">
                                            낙찰금액
                                            {renderSortIcon("winningPrice")}
                                        </span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {displayResults.map((r, i) => (
                                <tr
                                    key={i}
                                    className="slow-animation border-b hover:bg-gray-50 cursor-pointer"
                                    onClick={() => setSelected(r)}
                                >
                                    <td className="px-3 py-2">{r.bidTitle}</td>
                                    <td className="px-3 py-2">
                                        {r.bidOpenDate ? r.bidOpenDate.substring(0, 10) : '-'}
                                    </td>
                                    <td className="px-3 py-2">
                                        {r.winningCompanyName || '-'}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {r.winningPrice
                                            ? `${r.winningPrice.toLocaleString()}원`
                                            : '[낙찰 금액을 제공하지 않습니다]'}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </section>
                ) : (
                    !progress && (
                        <section className="text-gray-500 text-sm bg-white p-4 rounded-xl shadow border flex items-center gap-2">
                            <Loader className="animate-spin w-4 h-4" /> 입찰 결과 목록 로딩 중입니다...
                        </section>
                    )
                )}

                {/* 모달 */}
                <BidDetailModal
                    bidNumber={selected?.bidNoticeNumber}
                    bidNoticeOrder={selected?.bidNoticeOrder}
                    isOpen={!!selected}
                    onClose={() => setSelected(null)}
                />
            </div>
        </Spin>
    );
};

export default CompanySearchPage;